import * as amplitude from "@amplitude/analytics-browser";
import { useCallback } from "react";
import moment from "moment";

import { useProtocolsRouter } from "./useProtocolsRouter";
import { useTrialsRouter } from "./useTrialsRouter";
import { useStable } from "./useStable";
import { useAuth } from "../../lib/auth/AuthContext";
import { getCookieBotConsentStatistics } from "../utils/cookieConsent";
import { useAppState } from "../../lib/appContext/AppContext";
import i18n from "../../i18n";

type TrackingEmitter = (
  additionalData?: Record<string, unknown>
) => Promise<void>;

type TrackingProperties = Record<string, unknown>;

interface ITrackingState extends TrackingProperties {
  module: string;
  email: string | undefined;
  season: number;
  organization: string | undefined;
  farmId: string;
}

interface IOrganizationIdentity {
  id?: string;
  name?: string;
}

const getTrackingStatePayload = (
  trackingState: ITrackingState,
  hasStatisticsConsent: boolean
): TrackingProperties => {
  if (hasStatisticsConsent) {
    return trackingState;
  }

  const sanitizedState = { ...trackingState };
  delete sanitizedState.email;
  return sanitizedState;
};

const getTrackingEventName = (
  defaultName: string,
  stableProperties?: TrackingProperties,
  additionalData?: TrackingProperties
): string => {
  return (
    (typeof additionalData?.event === "string"
      ? additionalData.event
      : undefined) ??
    (typeof stableProperties?.event === "string"
      ? stableProperties.event
      : undefined) ??
    defaultName
  );
};

const getDeviceType = (): string => {
  const userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent;

  if (/ipad|tablet/i.test(userAgent)) {
    return "tablet";
  }

  if (/mobile|iphone|ipod|android.*mobile/i.test(userAgent)) {
    return "mobile";
  }

  return userAgent ? "desktop" : "unknown";
};

/**
 * to track events and actions in amplitude, we are using this hook to get the common properties and
 * emit the event to amplitude.
 * @param name The name of the event to be tracked.
 * @param properties The properties associated with the event.
 * @param module The module from which the event is being tracked.
 * @returns A function that can be used to emit tracking events to Amplitude.
 */
export const useAmplitudeLogging = (
  name: string,
  properties?: TrackingProperties | undefined
): TrackingEmitter => {
  const { stateValue } = useAppState();
  const { user } = useAuth();
  const language = i18n.language;
  const stableProperties = useStable(properties);
  const moduleName =
    typeof stableProperties?.module === "string"
      ? stableProperties.module.toLowerCase()
      : "";
  const isProtocolModule = moduleName.includes("protocol");
  const trialsRoute = useTrialsRouter();
  const protocolsRoute = useProtocolsRouter();
  const preferredRoute = isProtocolModule ? protocolsRoute : trialsRoute;
  const fallbackRoute = isProtocolModule ? trialsRoute : protocolsRoute;
  const propertyId =
    preferredRoute.farmId ?? fallbackRoute.farmId ?? stateValue?.farmValue?.id;
  const organizationId =
    preferredRoute.orgId ?? fallbackRoute.orgId ?? stateValue?.orgValue?.id;
  const trackingState = useStable<ITrackingState>({
    module:
      typeof stableProperties?.module === "string"
        ? stableProperties.module
        : "",
    email: user?.email,
    season: new Date().getFullYear(),
    organization: organizationId,
    farmId: propertyId ?? "",
  });
  const emit = useCallback(
    async (additionalData?: TrackingProperties): Promise<void> => {
      const hasStatisticsConsent = getCookieBotConsentStatistics();
      const statePayload = getTrackingStatePayload(
        trackingState,
        hasStatisticsConsent
      );
      const userProperties = {
        user_id: hasStatisticsConsent ? user?.id ?? null : null,
        user_role: hasStatisticsConsent ? user?.role ?? null : null,
        country: user?.country ?? user?.country_code ?? null,
      };

      const payload = Object.freeze({
        state: statePayload,
        time: moment().format("MM/DD/YYYY h:mm a"),
        language,
        ...stableProperties,
        ...additionalData,
        ...userProperties,
        device: getDeviceType(),
      });
      const eventName = getTrackingEventName(
        name,
        stableProperties,
        additionalData
      );

      if (eventName) {
        amplitude.logEvent(eventName, payload);
      }

      await Promise.resolve();
    },
    [
      language,
      name,
      stableProperties,
      trackingState,
      user?.country,
      user?.country_code,
      user?.id,
      user?.role,
    ]
  );

  return emit;
};

/**
 * Track a generic-level _event_.
 * hook.
 */
export const useTrackingEvent = (
  properties?: Record<string, unknown> | undefined
): TrackingEmitter => {
  // we are already passing the event name in the properties so to follow the useAmplitudeLogging standards we are passing empty strings here.
  return useAmplitudeLogging("", properties);
};

/**
 * Track an application-level _event_. (Something that occurs _not_ in response to a user action).
 * Only use for top-level events; if you are specifically within a particular module, use a more specific tracking
 * hook.
 */
export const useTrackApplicationEvent = (
  name: string,
  properties?: Record<string, unknown> | undefined
): TrackingEmitter => {
  return useAmplitudeLogging(`event: ${name}`, properties);
};

/**
 * Track an application-level _action_. (Something that a user initiates).
 * Only use for top-level actions; if you are specifically within a particular module, use a more specific tracking
 * hook.
 */
export const useTrackApplicationAction = (
  name: string,
  properties?: Record<string, unknown> | undefined
): TrackingEmitter => {
  return useAmplitudeLogging(`action: ${name}`, properties);
};

/**
 * Set user properties when the organization changes.
 * @param org The organization identity.
 */
export const setUserPropertyByOrgChange = (
  org: IOrganizationIdentity
): void => {
  const updatedIdentity = new amplitude.Identify();
  org.id && updatedIdentity.set("org_id", org.id);
  org.name && updatedIdentity.set("org_name", org.name);
  updatedIdentity.set("date_time", new Date().toISOString());
  amplitude.identify(updatedIdentity);
};
